import React, { createContext, FC, Fragment, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import { ContentFragment } from 'api';
import { useFeatures } from 'hooks';

interface IContext {
  open: boolean;
  content: ContentFragment;
}

export interface IDiscussionsContext {
  context: IContext;
  onOpen: (content?: ContentFragment) => void;
  onClose: () => void;
}

const useStyles = makeStyles(theme => ({
  '@global': {
    '.str-chat *, .str-chat *::after, .str-chat *::before': {
      fontFamily: theme.typography.fontFamily,
    },
    '.str-chat.messaging': {
      backgroundColor: 'transparent',
    },
    '.str-chat__avatar--rounded': {
      marginRight: 0,
      borderRadius: 4,
    },
    '.str-chat__input-flat': {
      padding: `${theme.spacing()}px ${theme.spacing(2)}px`,
    },
    '.str-chat__input-flat-emojiselect': {
      top: `calc(100% - ${theme.spacing(6)}px)`,
      left: theme.spacing(2),
    },
    '.str-chat__input-flat .str-chat__textarea > textarea': {
      paddingLeft: theme.spacing(7),
      paddingRight: theme.spacing(7),
    },
    '.str-chat__modal': {
      opacity: 1,
      background: 'rgba(0,0,0,0.5)',
    },
    '.str-chat__modal .str-chat__input-emojiselect': {
      height: 14,
      marginRight: theme.spacing(),
    },
    '.str-chat__modal .str-chat__input-emojiselect, .str-chat__modal .str-chat__input-fileupload': {
      top: 4,
    },
    '.react-images__blanket': {
      backgroundColor: 'rgba(0, 0, 0, 0.5) !important',
      zIndex: `${theme.zIndex.modal + 1} !important`,
    },
    '.react-images__positioner': {
      zIndex: `${theme.zIndex.modal + 2} !important`,
    },
    '.str-chat__loading-channels': {
      background: 'transparent',
    },
    '.str-chat__load-more-button': {
      marginTop: theme.spacing(),
    },
  },
}));

const DiscussionsContext = createContext<IDiscussionsContext>({
  context: {
    open: false,
    content: null,
  },
  onOpen: () => undefined,
  onClose: () => undefined,
});

const DiscussionsProvider: FC = ({ children }) => {
  const { comments } = useFeatures();
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState<ContentFragment>();

  useStyles();

  if (!comments) {
    return <Fragment>{children}</Fragment>;
  }

  async function handleOpen(nextContent: ContentFragment) {
    setOpen(true);
    setContent(nextContent);
  }

  function handleClose() {
    setOpen(false);
    setContent(null);
  }

  const props: IDiscussionsContext = {
    context: {
      open,
      content,
    },
    onOpen: handleOpen,
    onClose: handleClose,
  };

  return <DiscussionsContext.Provider value={props}>{children}</DiscussionsContext.Provider>;
};

export { DiscussionsContext, DiscussionsProvider };
